import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { CaretLeft, CaretRight, Lightning, Newspaper, Gift, ChartLineUp, Stack, MagnifyingGlass, X, Sparkle, DotsSixVertical } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";

const STATUS_COLORS = {
  draft: "bg-[var(--bg-hover)] text-[var(--text-muted)] border-[var(--border)]",
  in_review: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  published: "bg-blue-500/10 text-blue-400 border-blue-500/30",
};

const STATUS_ORDER = ["draft", "in_review", "approved", "published"];

const TYPE_ICONS = {
  "Social Post": Lightning,
  "Email": Lightning,
  "Newsletter Issue": Newspaper,
  "Lead Magnet": Gift,
  "Content Sequence": ChartLineUp,
  "Bulk": Stack,
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getDaysInMonth(year, month) { return new Date(year, month, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month - 1, 1).getDay(); }

export default function CalendarPage() {
  const { api } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // Drag state
  const [dragItem, setDragItem] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);

  // Auto-populate state
  const [studioPackages, setStudioPackages] = useState([]);
  const [showStudioPicker, setShowStudioPicker] = useState(false);
  const [loadingStudio, setLoadingStudio] = useState(false);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/history/calendar?month=${month}&year=${year}`);
      setItems(resp.data.items || []);
    } catch { toast.error("Failed to load calendar"); }
    finally { setLoading(false); }
  }, [api, month, year]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const navigateMonth = (dir) => {
    let m = month + dir, y = year;
    if (m < 1) { m = 12; y--; } if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y); setSelectedDay(null);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const filteredItems = statusFilter === "all" ? items : items.filter(i => (i.status || "draft") === statusFilter);

  const itemsByDay = {};
  filteredItems.forEach((item) => {
    if (!item.planned_publish_date) return;
    const day = parseInt(item.planned_publish_date.split("-")[2], 10);
    if (!itemsByDay[day]) itemsByDay[day] = [];
    itemsByDay[day].push(item);
  });

  const today = now.getDate();
  const isCurrentMonth = now.getMonth() + 1 === month && now.getFullYear() === year;

  const handleSearchContent = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try { const resp = await api.get(`/history?search=${encodeURIComponent(q)}&limit=10`); setSearchResults(resp.data.items || []); }
    catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const assignDate = async (historyId, date) => {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(date).padStart(2, "0")}`;
    try {
      await api.patch(`/history/${historyId}/publish-date`, { planned_publish_date: dateStr });
      toast.success("Content scheduled");
      setSearchOpen(false); setSearchQuery(""); setSearchResults([]); setSelectedDay(null); setShowStudioPicker(false);
      fetchCalendar();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to schedule"); }
  };

  const removeDate = async (historyId) => {
    try {
      await api.patch(`/history/${historyId}/publish-date`, { planned_publish_date: null });
      toast.success("Date removed"); fetchCalendar();
    } catch { toast.error("Failed to remove date"); }
  };

  const changeStatus = async (historyId, newStatus) => {
    try {
      await api.patch(`/history/${historyId}`, { status: newStatus });
      toast.success(`Status changed to ${newStatus.replace("_", " ")}`);
      fetchCalendar();
    } catch { toast.error("Failed to change status"); }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    setDragItem(item);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.id);
  };

  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverDay(day);
  };

  const handleDragLeave = () => { setDragOverDay(null); };

  const handleDrop = async (e, day) => {
    e.preventDefault();
    setDragOverDay(null);
    if (!dragItem) return;
    await assignDate(dragItem.id, day);
    setDragItem(null);
  };

  // Auto-populate from Content Studio
  const fetchStudioPackages = async () => {
    setLoadingStudio(true);
    try {
      const resp = await api.get("/content-studio/packages?limit=10");
      setStudioPackages(resp.data.packages || []);
    } catch (e) { console.error("Failed to fetch studio packages:", e); }
    setLoadingStudio(false);
  };

  return (
    <NoBusinessGuard>
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <div className="pt-4 px-4 pb-12" data-testid="calendar-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button onClick={() => navigateMonth(-1)} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid="calendar-prev-month"><CaretLeft size={20} /></button>
              <h1 className="text-xl font-semibold text-[var(--text-primary)] font-['JetBrains_Mono']" data-testid="calendar-month-label">{MONTHS[month - 1]} {year}</h1>
              <button onClick={() => navigateMonth(1)} className="p-2 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" data-testid="calendar-next-month"><CaretRight size={20} /></button>
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              {/* Status filter */}
              <div className="flex gap-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-0.5 shrink-0" data-testid="status-filter">
                <button onClick={() => setStatusFilter("all")} className={`px-2.5 py-1.5 sm:py-1 rounded-md text-[10px] font-medium transition-all ${statusFilter === "all" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)]"}`}>All</button>
                {STATUS_ORDER.map(s => (
                  <button key={s} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1.5 sm:py-1 rounded-md text-[10px] font-medium transition-all capitalize whitespace-nowrap ${statusFilter === s ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)]"}`} data-testid={`filter-${s}`}>{s.replace("_", " ")}</button>
                ))}
              </div>
              <button onClick={() => { setMonth(now.getMonth() + 1); setYear(now.getFullYear()); }} className="px-3 py-2 sm:py-1.5 rounded-md text-xs text-[var(--text-secondary)] border border-[var(--border-strong)] hover:text-[var(--text-primary)] transition-colors shrink-0" data-testid="calendar-today-btn">Today</button>
            </div>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map((d) => <div key={d} className="text-center text-[10px] sm:text-xs font-['JetBrains_Mono'] text-[var(--text-muted)] py-2">{window.innerWidth < 640 ? d[0] : d}</div>)}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 border-l border-t border-[var(--border)]" data-testid="calendar-grid">
            {[...Array(firstDay)].map((_, i) => <div key={`empty-${i}`} className="min-h-[60px] sm:min-h-[100px] border-r border-b border-[var(--border)] bg-[var(--bg-base)]" />)}

            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dayItems = itemsByDay[day] || [];
              const isToday = isCurrentMonth && day === today;
              const isDragOver = dragOverDay === day;

              return (
                <div
                  key={day}
                  className={`min-h-[60px] sm:min-h-[100px] border-r border-b border-[var(--border)] p-1 sm:p-1.5 cursor-pointer hover:bg-[var(--bg-hover)] transition-colors ${isToday ? "bg-[#FF4400]/5" : ""} ${isDragOver ? "bg-[#FF4400]/10 border-[#FF4400]" : ""}`}
                  onClick={() => { setSelectedDay(selectedDay === day ? null : day); setSearchOpen(false); setShowStudioPicker(false); }}
                  onDragOver={(e) => handleDragOver(e, day)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, day)}
                  data-testid={`calendar-day-${day}`}
                >
                  <span className={`text-[10px] sm:text-xs font-['JetBrains_Mono'] inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full ${isToday ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)]"}`}>{day}</span>
                  <div className="mt-0.5 sm:mt-1 space-y-0.5">
                    {dayItems.slice(0, window.innerWidth < 640 ? 1 : 3).map((item) => {
                      const Icon = TYPE_ICONS[item.content_type] || Lightning;
                      return (
                        <div key={item.id}
                          draggable onDragStart={(e) => handleDragStart(e, item)}
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border cursor-grab active:cursor-grabbing truncate ${STATUS_COLORS[item.status || "draft"]}`}
                          data-testid={`calendar-chip-${item.id}`} title={item.title}
                        >
                          <DotsSixVertical size={8} className="shrink-0 opacity-40" />
                          <Icon size={10} className="shrink-0" />
                          <span className="truncate">{item.title.length > 18 ? item.title.slice(0, 18) + "..." : item.title}</span>
                        </div>
                      );
                    })}
                    {dayItems.length > 3 && <span className="text-[10px] text-[var(--text-muted)]">+{dayItems.length - 3} more</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Day popover */}
          <AnimatePresence>
            {selectedDay && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="mt-4 bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg p-4" data-testid="day-popover">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-sm font-medium text-[var(--text-primary)] font-['JetBrains_Mono']">{MONTHS[month - 1]} {selectedDay}, {year}</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSearchOpen(!searchOpen)}
                      className="px-3 py-1.5 rounded-md text-xs bg-[#FF4400] text-white hover:bg-[#FF6633] transition-colors" data-testid="schedule-content-btn">
                      + Schedule Content
                    </button>
                    <button onClick={() => { if (!showStudioPicker) fetchStudioPackages(); setShowStudioPicker(!showStudioPicker); }}
                      className="px-3 py-1.5 rounded-md text-xs bg-violet-600 text-white hover:bg-violet-500 transition-colors flex items-center gap-1" data-testid="auto-populate-btn">
                      <Sparkle size={12} /> From Studio
                    </button>
                    <button onClick={() => setSelectedDay(null)} className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"><X size={16} /></button>
                  </div>
                </div>

                {/* History search */}
                {searchOpen && (
                  <div className="mb-3 space-y-2" data-testid="schedule-search">
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      <input type="text" value={searchQuery} onChange={(e) => handleSearchContent(e.target.value)}
                        placeholder="Search history to schedule..." autoFocus
                        className="w-full pl-9 pr-3 py-2 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-md text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="schedule-search-input" />
                    </div>
                    {searching && <p className="text-xs text-[var(--text-muted)]">Searching...</p>}
                    {searchResults.length > 0 && (
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {searchResults.map((r) => (
                          <button key={r.history_id} onClick={() => assignDate(r.history_id, selectedDay)}
                            className="w-full text-left px-3 py-2 rounded-md text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors flex items-center justify-between" data-testid={`schedule-result-${r.history_id}`}>
                            <span className="truncate">{r.title || r.topic}</span>
                            <span className="text-[10px] text-[var(--text-muted)] shrink-0 ml-2">{r.content_type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Studio package picker */}
                <AnimatePresence>
                  {showStudioPicker && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                      className="mb-3 overflow-hidden" data-testid="studio-picker">
                      <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-400">Content Studio Packages</p>
                        {loadingStudio ? (
                          <p className="text-xs text-[var(--text-muted)]">Loading...</p>
                        ) : studioPackages.length === 0 ? (
                          <p className="text-xs text-[var(--text-muted)]">No packages yet. Create one in Content Studio.</p>
                        ) : (
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {studioPackages.map(pkg => (
                              <button key={pkg.package_id} onClick={() => {
                                toast.info("Studio packages can be scheduled after saving to history. Use the Create tab to generate and then schedule.");
                                setShowStudioPicker(false);
                              }}
                                className="w-full text-left px-3 py-2 rounded-md text-xs text-[var(--text-secondary)] hover:bg-violet-500/10 transition-colors" data-testid={`studio-pkg-${pkg.package_id}`}>
                                <span className="truncate block">{pkg.idea}</span>
                                <span className="text-[10px] text-[var(--text-muted)]">{pkg.channels?.join(", ")} · {new Date(pkg.created_at).toLocaleDateString()}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Items for this day */}
                {(itemsByDay[selectedDay] || []).length > 0 ? (
                  <div className="space-y-2">
                    {(itemsByDay[selectedDay] || []).map((item) => {
                      const Icon = TYPE_ICONS[item.content_type] || Lightning;
                      const currentStatus = item.status || "draft";
                      return (
                        <div key={item.id} className="flex items-center justify-between p-2 rounded-md bg-[var(--bg-base)] border border-[var(--border)] gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Icon size={14} className="text-[var(--text-muted)] shrink-0" />
                            <span className="text-sm text-[var(--text-primary)] truncate">{item.title}</span>
                          </div>
                          {/* Status selector */}
                          <select value={currentStatus} onChange={(e) => changeStatus(item.id, e.target.value)}
                            className={`text-[10px] px-2 py-1 rounded-full border bg-transparent cursor-pointer ${STATUS_COLORS[currentStatus]}`} data-testid={`status-select-${item.id}`}>
                            {STATUS_ORDER.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                          </select>
                          <button onClick={() => removeDate(item.id)} className="p-1 rounded text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0" data-testid={`remove-date-${item.id}`} title="Remove from calendar"><X size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                ) : !searchOpen && !showStudioPicker ? (
                  <p className="text-xs text-[var(--text-muted)]">No content scheduled for this day.</p>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
    </NoBusinessGuard>
  );
}
